import React, { useState } from 'react';
import { Edit, Trash2, Tool as ToolIcon, Server, Search } from 'react-feather';
import Modal from '../Modal';

// Deterministic tile colour + initials from the tool name. Tools have no
// logos, so this gives every card a stable visual anchor that survives
// reloads and reordering.
const AVATAR_VARIANTS = 6;

const avatarIndex = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 100000;
  }
  return hash % AVATAR_VARIANTS;
};

// Tool names are identifiers like `send_email_notification` or `bookMeeting`.
// Split on the word boundaries those conventions create so the monogram is
// meaningful ("SE", "BM") rather than just the first two letters.
const initials = (name = '') => {
  const words = name.split(/[^a-zA-Z0-9]+|(?=[A-Z][a-z])/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2);
  return (words[0][0] + words[1][0]);
};

// Parameters arrive in two shapes: a JSON Schema object (built-in tools) or a
// list of definitions (custom tools). Normalise both to one row format so the
// detail table can render them identically.
const normalizeParameters = (parameters) => {
  if (!parameters) return [];

  if (Array.isArray(parameters)) {
    return parameters.map(p => ({
      name: p.name,
      type: p.type || 'string',
      description: p.description || '',
      required: !!p.required,
      enumValues: p.enum || null,
      defaultValue: p.default,
    }));
  }

  const props = parameters.properties || {};
  const required = parameters.required || [];
  return Object.entries(props).map(([name, prop]) => ({
    name,
    type: prop.type || 'string',
    description: prop.description || '',
    required: required.includes(name),
    enumValues: prop.enum || null,
    defaultValue: prop.default,
  }));
};

const ToolsList = ({ tools, onEdit, onDelete, isAdmin, deletingToolName, isFiltered, onClearFilter }) => {
  const [activeTool, setActiveTool] = useState(null);

  if (tools.length === 0) {
    // Two different empty states: nothing exists yet vs. nothing matched the
    // search. Showing "add your first tool" to someone who has ten tools and a
    // typo in the search box is misleading.
    if (isFiltered) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Search size={20} />
          </div>
          <h3 className="empty-state-title">No matching tools</h3>
          <p>No tool matches your search. Try a different name or description.</p>
          {onClearFilter && (
            <button type="button" className="empty-state-action" onClick={onClearFilter}>
              Clear search
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <ToolIcon size={20} />
        </div>
        <h3 className="empty-state-title">No tools yet</h3>
        <p>Tools let your agents take action, send an email, look up a record,
          or book a meeting. Add your first one to get started.</p>
      </div>
    );
  }

  const activeParams = activeTool ? normalizeParameters(activeTool.parameters) : [];

  return (
    <>
      <div className="tools-list">
        {tools.map(tool => {
          const isThisToolDeleting = tool.name === deletingToolName;
          const canManage = isAdmin || tool.is_custom;
          const paramCount = normalizeParameters(tool.parameters).length;

          return (
            // The whole card opens the detail dialog. It is a button so it is
            // reachable by keyboard and announced as actionable; the Edit and
            // Delete controls stopPropagation so they do not also open it.
            <button
              type="button"
              className={`tool-card ${isThisToolDeleting ? 'tool-card--deleting' : ''}`}
              key={tool.id}
              onClick={() => setActiveTool(tool)}
              disabled={isThisToolDeleting}
              aria-label={`View details for ${tool.name}`}
            >
              <div className="tool-header">
                <div
                  className={`tool-avatar tool-avatar--${avatarIndex(tool.name)}`}
                  aria-hidden="true"
                >
                  {initials(tool.name)}
                </div>
                {/* Title only, the Custom/Built-in badge moved to the footer
                    beside the parameter count, which unclutters the header
                    and lets the name sit centred against the icon. */}
                <h3 className="tool-title" title={tool.name}>{tool.name}</h3>
                <div className="tool-actions">
                  {canManage && (
                    <span
                      className="icon-button edit"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onEdit(tool); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          onEdit(tool);
                        }
                      }}
                      aria-label={`Edit ${tool.name}`}
                      title="Edit"
                    >
                      <Edit size={16} />
                    </span>
                  )}
                  {canManage && (
                    <span
                      className="icon-button delete"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onDelete(tool); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          onDelete(tool);
                        }
                      }}
                      aria-label={`Delete ${tool.name}`}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </span>
                  )}
                </div>
              </div>

              <div className="tool-body">
                <p className={`tool-description ${tool.description ? '' : 'is-empty'}`}>
                  {tool.description || 'No description provided.'}
                </p>

                <div className="tool-footer">
                  <span className={`tool-badge ${tool.is_custom ? 'custom' : 'builtin'}`}>
                    {tool.is_custom ? 'Custom' : 'Built-in'}
                  </span>
                  <span className="tool-param-chip">
                    {paramCount} {paramCount === 1 ? 'parameter' : 'parameters'}
                  </span>
                  {tool.is_custom && tool.server_url && (
                    <span className="tool-endpoint" title={tool.server_url}>
                      <Server size={12} aria-hidden="true" />
                      <span className="tool-endpoint-text">{tool.server_url}</span>
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Modal
        isOpen={!!activeTool}
        onClose={() => setActiveTool(null)}
        title={activeTool ? activeTool.name : ''}
      >
        {activeTool && (
          <div className="tool-detail">
            <div className="tool-detail-summary">
              <span className={`tool-badge ${activeTool.is_custom ? 'custom' : 'builtin'}`}>
                {activeTool.is_custom ? 'Custom' : 'Built-in'}
              </span>
              <p className={`tool-detail-description ${activeTool.description ? '' : 'is-empty'}`}>
                {activeTool.description || 'No description provided.'}
              </p>
            </div>

            {activeTool.is_custom && activeTool.server_url && (
              <div className="tool-detail-block">
                <h4 className="tool-detail-heading">Endpoint</h4>
                <code className="tool-detail-endpoint">{activeTool.server_url}</code>
              </div>
            )}

            <div className="tool-detail-block">
              <h4 className="tool-detail-heading">
                Parameters
                <span className="parameters-count">{activeParams.length}</span>
              </h4>

              {activeParams.length === 0 ? (
                <p className="tool-detail-empty">This tool takes no parameters.</p>
              ) : (
                <ul className="param-list">
                  {activeParams.map(param => (
                    <li className="param-row" key={param.name}>
                      <div className="param-row-head">
                        <code className="param-name">{param.name}</code>
                        <span className="param-type">{param.type}</span>
                        {param.required ? (
                          <span className="param-flag param-flag--required">Required</span>
                        ) : (
                          <span className="param-flag">Optional</span>
                        )}
                      </div>
                      {param.description && (
                        <p className="param-description">{param.description}</p>
                      )}
                      {(param.enumValues || param.defaultValue !== undefined) && (
                        <div className="param-extra">
                          {param.enumValues && (
                            <span className="param-extra-item">
                              <span className="param-extra-label">Options</span>
                              {param.enumValues.join(', ')}
                            </span>
                          )}
                          {param.defaultValue !== undefined && param.defaultValue !== null && (
                            <span className="param-extra-item">
                              <span className="param-extra-label">Default</span>
                              {String(param.defaultValue)}
                            </span>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ToolsList;
